import { createSlice, current, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import { toast } from "react-toastify";
import { fetchLocations, createLocation, updateLocation, deleteLocation } from "./locationAsyncThunk";
import { DeleteLocation } from "../../../types/LocationTypes";
import { Location } from "../../../types";
import { RequestResults } from "../../common/RequestStatus";

interface LocationState {
  data: Location[];
  status: RequestResults;
  loading: boolean;
  error: string | null;
}

const initialState: LocationState = {
  data: [],
  status: RequestResults.idle,
  loading: false,
  error: null,
};

// Slice
const locationSlice = createSlice({
  name: "location",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Fetch Locations
    builder
      .addCase(fetchLocations.pending, (state) => {
        state.status = RequestResults.pending;
        state.error = null;
      })
      .addCase(fetchLocations.fulfilled, (state, action: PayloadAction<Location[]>) => {
        state.status = RequestResults.success;
        state.data = action.payload;
      })
      .addCase(fetchLocations.rejected, (state, action) => {
        state.status = RequestResults.failed;
        toast.error(action.payload || "Failed to fetch locations");
        state.error = action.payload || "Failed to fetch locations";
      });

    // Add Location
    builder
      .addCase(createLocation.pending, (state) => {
        state.loading = true;
      })
      .addCase(createLocation.fulfilled, (state, action: PayloadAction<Location>) => {
        state.loading = false;
        state.data.push(action.payload);
      })
      .addCase(createLocation.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to add Location");
        state.error = action.payload || "Failed to add Location";
      });

    // Update Location
    builder
      .addCase(updateLocation.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateLocation.fulfilled, (state, action: PayloadAction<Location>) => {
        const index = state.data.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.data[index] = action.payload;
        }
        state.loading = false;
      })
      .addCase(updateLocation.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to update Location");
        state.error = action.payload || "Failed to update Location";
      });

    // Delete Location
    builder
      .addCase(deleteLocation.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteLocation.fulfilled, (state, action: PayloadAction<DeleteLocation>) => {
        const index = state.data.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.data = state.data.filter((item) => item.id !== action.payload.id);
        }
        state.loading = false;
      })
      .addCase(deleteLocation.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to delete Location");
        state.error = action.payload || "Failed to delete Location";
      });
  },
});

export const selectLocation = (state: RootState) => state.location;

export default locationSlice.reducer;
